<template lang="pug">
.new-arrival-family-history-list
  // message list
  FamilyReceiveMessageTable(
    v-model="messageItems"
    :fields="messageFields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isBusy="isBusy"
    @onDetail="onDetail"
  )
  // pagenation
  .d-flex.flex-row.justify-content-center
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isBusy"
      @change="selectPage"
      hide-goto-end-buttons
    )
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import Setting from "../../../../public/setting.json";

import OpinionsService, { OpinionGetModel } from "@/commons/services/Opinions";

import FamilyReceiveMessageTable, { FamilyReceiveMessageTableField, FamilyReceiveMessageTableItem } from "@/mobile/components/tables/FamilyReceiveMessageTable.vue";

/**
 * ### 未確認のみの受信メッセージリスト(家族)コンポーネント
 */
@Component({
  components: {
    FamilyReceiveMessageTable
  }
})
export default class FamilyReceiveMessageList extends Vue {
  /** 意見サービス */
  @Inject("OpinionsService") opinionsService!: OpinionsService;

  /** 読み込み中モーダルメッセージ */
  loadingMessage: string = "読込み中";
  /** 読込み中か否か */
  isBusy: boolean = true;

  /** 受信メッセージ列名 */
  messageFields: Array<FamilyReceiveMessageTableField> = [];
  /** 受信メッセージの情報 */
  messageItems: Array<FamilyReceiveMessageTableItem> = [];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** メッセージ履歴1ページあたりの表示数 */
  perPage: number = Number(Setting.opinion.perPage);
  /** ページングの全体行数 */
  rows: number = 0;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.opinion.pageRange;

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const messageFields: Array<FamilyReceiveMessageTableField> = this.createMessageFields();
    this.messageFields = messageFields;

    // 動的に変更する画面情報を作成する
    this.messageItems = [];
    this.currentRows = this.messageItems.length;
    await this.createView();

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createMessageFields(): Array<FamilyReceiveMessageTableField> {
    let fields: Array<FamilyReceiveMessageTableField> = [];
    fields.push({ key: "message", label: "", sortable: false, sortDirection: "desc", class: "" });

    return fields;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // テーブルに渡す各プロパティを作成する
    const messages: Array<OpinionGetModel> = await this.getOpinions();
    const messageItems: Array<FamilyReceiveMessageTableItem> = this.createItems(messages);
    this.messageItems = this.messageItems.concat(messageItems);
    this.currentRows = this.messageItems.length;

    this.isBusy = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows(): Promise<void> {
    try {
      const params: any = {
        onlyUnconfirmed: "true"
      };
      const count: number = await this.opinionsService.getCount(userStore, params);

      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 表示対象の意見を取得する
   */
  public async getOpinions(): Promise<Array<OpinionGetModel>> {
    const startIndex: number = this.currentRows;
    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象のよりも多めに取得しておく
    const endIndex: number = this.perPage * (this.currentPage * this.pageRange);
    const params: any = {
      startIndex: startIndex,
      endIndex: endIndex,
      order: "desc",
      onlyUnconfirmed: "true"
    };
    const response: Array<OpinionGetModel> = await this.opinionsService.gets(userStore, params);

    // 完了しているか否かを設定する
    // 完了日を見て判断する
    for (let opinion of response) {
      if (opinion.functionContents) {
        if (opinion.functionContents.finished) {
          opinion.functionContents.isFinished = true;
        } else {
          opinion.functionContents.isFinished = false;
        }
      }
    }

    return response;
  }

  /**
   * ### 受信メッセージの行を作成する
   */
  public createItems(opinions: Array<OpinionGetModel>): Array<FamilyReceiveMessageTableItem> {
    let items: Array<FamilyReceiveMessageTableItem> = [];

    for (const opinion of opinions) {
      items.push({
        functionContents: opinion.functionContents,
        residentId: opinion.residentId,
        residentName: opinion.residentName,
        familyId: opinion.familyId,
        familyName: opinion.familyName,
        datetime: Number(opinion.datetime),
        groupId: opinion.groupId,
        caseId: opinion.caseId
      });
    }

    return items;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    // 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }

  /**
   * ### 詳細ボタン押下イベント
   */
  public onDetail(item: FamilyReceiveMessageTableItem): void {
    this.$emit("onDetail", item);

    return;
  }
}
</script>
